// Quantiv Media HR — React root. Mounted by index.js when REACT_APP_MODE=hr (default).
// Routes match the WP plugin sidebar exactly (employee screens E1-E8 + admin
// screens 1-12). Export `HRRoutes` if you ever need to embed this app inside
// another React shell.

import React from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import UserLogin from "./Pages/Login";
import AcceptInvite from "./Pages/Invite/AcceptInvite";
import Profile from "./Pages/Profile/Profile";
import UserProfile from "./Pages/Profile/UserProfile";
import AdminPanel from "./Pages/Admin/AdminPanel";
import PasswordResetsPage from "./Pages/Admin/PasswordResetsPage";
import InternsAdminPage from "./Pages/Admin/InternsAdminPage";
import HRAdmin from "./Pages/HR/HRAdmin";
import LeaveBalancesPage from "./Pages/Leaves/LeaveBalancesPage";
import DashboardRouter from "./Pages/Dashboard/DashboardRouter";
import MyAttendancePage from "./Pages/Attendance/MyAttendancePage";
import AttendancePage from "./Pages/Attendance/AttendancePage"; // admin month/day/etc
import RegularisationPage from "./Pages/Regularisation/RegularisationPage";           // employee
import RegularisationAdminPage from "./Pages/Regularisation/RegularisationAdminPage"; // admin inbox
import MyLeavesPage from "./Pages/Leaves/MyLeavesPage";
import LeavesPage from "./Pages/Leaves/LeavesPage";              // admin Leave Requests inbox
import WfhPage from "./Pages/WFH/WfhPage";
import WfhAdminPage from "./Pages/WFH/WfhAdminPage";
import HolidaysPage from "./Pages/Holidays/HolidaysPage";
import NoticesPage from "./Pages/Notices/NoticesPage";
import PromptsPage from "./Pages/Prompts/PromptsPage";
import SettingsPage from "./Pages/Settings/SettingsPage";
import PayrollPage from "./Pages/Payroll/PayrollPage";              // admin
import PayrollRunPage from "./Pages/Payroll/PayrollRunPage";        // admin — monthly run (Phase A2/A3)
import MyPayslipsPage from "./Pages/Payroll/MyPayslipsPage";       // employee
import ReportsPage from "./Pages/Reports/ReportsPage";              // admin
import AppShell from "./Components/AppShell";
import ErrorBoundary from "./Components/ErrorBoundary";

const RequireAuth = ({ children }) => {
  if (!localStorage.getItem("token")) return <Navigate to="/login" replace />;
  return children;
};
const RequireAdmin = ({ children }) => {
  if (!localStorage.getItem("token")) return <Navigate to="/login" replace />;
  if (localStorage.getItem("role") !== "SUPER_ADMIN") return <Navigate to="/dashboard" replace />;
  return children;
};
const RootRedirect = () => {
  if (localStorage.getItem("token")) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

// Pick employee vs admin attendance page based on role.
function AttendanceRouter() {
  const role = localStorage.getItem("role");
  return role === "SUPER_ADMIN" ? <AttendancePage /> : <MyAttendancePage />;
}
function LeavesRouter() {
  const role = localStorage.getItem("role");
  return role === "SUPER_ADMIN" ? <LeavesPage /> : <MyLeavesPage />;
}
function PayrollRouter() {
  const role = localStorage.getItem("role");
  return role === "SUPER_ADMIN" ? <PayrollPage /> : <MyPayslipsPage />;
}
function WfhRouter() {
  const role = localStorage.getItem("role");
  return role === "SUPER_ADMIN" ? <WfhAdminPage /> : <WfhPage />;
}
function RegularisationRouter() {
  const role = localStorage.getItem("role");
  return role === "SUPER_ADMIN" ? <RegularisationAdminPage /> : <RegularisationPage />;
}
// Wrappers around HRAdmin that open the right tab — admin sidebar has
// Staff / Departments / Designations as separate items (WP-style grouped
// menu), but the underlying admin views share one component.
function StaffAdminPage()        { return <HRAdmin defaultTab="staff" />; }
function DepartmentsAdminPage()  { return <HRAdmin defaultTab="departments" />; }
// Designations page hidden (org doesn't use designations). Restore with the route in <Routes> below.
// function DesignationsAdminPage() { return <HRAdmin defaultTab="designations" />; }

export const HRRoutes = () => (
  <Routes>
    <Route path="/" element={<RootRedirect />} />
    <Route path="/login" element={<UserLogin />} />
    <Route path="/invite/:token" element={<AcceptInvite />} />

    <Route element={<RequireAuth><AppShell /></RequireAuth>}>
      <Route path="/dashboard"  element={<DashboardRouter />} />
      <Route path="/attendance" element={<AttendanceRouter />} />
      <Route path="/regularisation" element={<RegularisationRouter />} />
      <Route path="/leaves"     element={<LeavesRouter />} />
      <Route path="/wfh"        element={<WfhRouter />} />
      <Route path="/holidays"   element={<HolidaysPage />} />
      <Route path="/notices"    element={<NoticesPage />} />
      <Route path="/profile"    element={<Profile />} />
      <Route path="/users/:id"  element={<UserProfile />} />
      <Route path="/prompts"    element={<PromptsPage />} />
      <Route path="/payroll"    element={<PayrollRouter />} />

      {/* Admin-only — mirrors the WP admin menu in class-dfhr-admin.php */}
      {/* People & Org: each sidebar item has its own URL but they all render
          HRAdmin with the appropriate tab pre-selected. */}
      <Route path="/hr"             element={<RequireAdmin><StaffAdminPage /></RequireAdmin>} />
      <Route path="/staff"          element={<RequireAdmin><StaffAdminPage /></RequireAdmin>} />
      <Route path="/interns"        element={<RequireAdmin><InternsAdminPage /></RequireAdmin>} />
      <Route path="/departments"    element={<RequireAdmin><DepartmentsAdminPage /></RequireAdmin>} />
      {/* Designations route hidden — org doesn't use designations. Restore to re-enable the page. */}
      {/* <Route path="/designations"   element={<RequireAdmin><DesignationsAdminPage /></RequireAdmin>} /> */}
      {/* Leave & WFH */}
      <Route path="/leave-balances" element={<RequireAdmin><LeaveBalancesPage /></RequireAdmin>} />
      {/* Payroll & Reports */}
      <Route path="/payroll-run"    element={<RequireAdmin><PayrollRunPage /></RequireAdmin>} />
      <Route path="/reports"        element={<RequireAdmin><ReportsPage /></RequireAdmin>} />
      {/* Settings + legacy admin console */}
      <Route path="/settings"       element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
      <Route path="/admin"          element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
      <Route path="/password-resets" element={<RequireAdmin><PasswordResetsPage /></RequireAdmin>} />
    </Route>

    <Route path="*" element={<RootRedirect />} />
  </Routes>
);

export default function HRApp() {
  return <div><ErrorBoundary><HRRoutes /></ErrorBoundary></div>;
}
